import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, Info, ListCheck, Loader } from "lucide-react";
import { verifyUpiBasicV2 } from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isNumeric, isAlpha, isAlphanumeric } from "validator";

function UpiBasicV2() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [upi, setUpiId] = useState(null);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlphaNumericdl = (value) => {
    if (!value) return;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(setIsJsonCopied(true));
    showSuccessToast("Text Copied");
  };
  // ---------------- VERIFY CONSUMER ----------------
  const handleVerifyUpi = async () => {
    if (!profileData) return;

    // const validateUpi = isAlphaNumericdl(upi);

    if (!upi) {
      return setInputError("⚠️ Please enter UPI ID");
    }

    try {
      setIsLoading(true);
      setResponse(null);
      setInputError(null);

      const res = await verifyUpiBasicV2({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        upi_id: upi.trim(),
      });

      if (res) {
        apiResponse = res;
        setResponse(apiResponse);
        console.log(res);
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "request successful");
      } else if (res?.status?.code === 422) {
        showErrorToast(response?.message || "Invalid format");
      } else if (
        res?.status === 500 ||
        res?.status?.code === 500 ||
        res?.data?.status === 500 ||
        res?.data?.status?.code === 500
      ) {
        showErrorToast(
          res?.error?.message ||
            res?.data?.error?.message ||
            res?.data?.message ||
            "server error",
        );
      } else if (!response.success) {
        showErrorToast(response?.data?.message || "verificaton failed");
      } else if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else {
        showErrorToast(res?.status?.message || "Verification failed");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md p-4 sm:p-6 mt-6 bg-white">
          <div className="flex flex-wrap items-center gap-4">
            {/* Mobile */}
            <div className="flex items-center gap-4">
              <label className=" font-bold">
                UPI ID: <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={upi}
                maxLength={20}
                onChange={(e) => setUpiId(e.target.value)}
                className="flex-1 border border-gray-400  rounded-md px-3 h-[38px]"
                placeholder="Eg: 9664ajk@hdfc"
              />
            </div>

            {/* input errors */}

            {inputError && (
              <div className="text-red-700 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex">
              <button
                disabled={isLoading || !upi}
                onClick={handleVerifyUpi}
                className="px-6 py-2 bg-gradient-to-br active:from-green-600 active:to-green-700 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {response && (
          <div
            className={` ${response.data?.status?.code === 500 || response.data?.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.error?.message ||
              response?.data?.error?.message ||
              response?.data?.message ||
              response?.message}
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-green-50 text-green-700 border border-green-400 p-5 mt-12 rounded-md">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <Info /> Important Instructions !!!
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid UPI ID. clicking the Verify button. After
              successful verification, the system will return the response in
              structured table and JSON format, including details associated
              with the provided UPI ID. You may review and copy the data for
              your reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl max-h-[600px] overflow-y-auto">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- USER DETAILS -------- */}
            <div className="bg-blue-600 text-white  p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2  font-bold mb-1">
                <ListCheck /> All Details
              </h2>
              <hr className="mb-8 w-full border-t border-gray-200" />

              {/* BASIC INFO */}
              {/* FULL RC DETAILS */}
              <div className="space-y-2 text-sm bg-blue-100 px-4 lg:px-6 py-4 rounded-lg">
                {/* Basic Info */}
                <Detail
                  label="Virtual Payment Address"
                  value={response?.data?.vpa_details?.vpa}
                />
                <Detail
                  label="Account Holder Name"
                  value={response?.data?.vpa_details?.account_holder_name}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default UpiBasicV2;

const Detail = ({ label, value }) => (
  <div className="flex">
    <span className="font-semibold text-[16px] text-blue-500 w-64">
      {label}:
    </span>
    <span className="text-black break-words">
      {value === null || value === "" ? "N/A" : value}
    </span>
  </div>
);
